import { Chip, Dialog, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addCompetitorShortName,
  approveCompetitor,
  fetchAllCompetitors,
  unapproveCompetitor,
} from "../../../api/competitors";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import { useSelector } from "react-redux";
import EditContent from "../../editContentTabs/editContent";
import EditContentTabs from "../../editContentTabs";
import {
  fetchMatchCenterContent,
  setMatchCenterContent,
} from "../../../api/matchCenter";
import {
  getContent,
  setContent,
} from "../../../api/content";
import { format } from "date-fns";
import PrivateComponent from "../../../providers/PrivateComponent/PrivateComponent";
import { PERMISSIONS } from "../../../utils/constants";
import hasPermissions from "../../../utils/hasPermissions";

const createColumns = (handleChange, handleClick, handleOpenDialog, toggleApprove) => {
  return [
    {
      field: "id",
      headerName: "ID",
      width: 100,
    },
    {
      field: "externalId",
      headerName: "External ID",
      width: 180,
    },
    {
      field: "sport",
      headerName: "Sport",
      width: 150,
    },
    {
      field: "name",
      headerName: "Name",
      width: 300,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 130,
      renderCell: ({ row }) =>
        !!row.createdAt
          ? format(new Date(row.createdAt), "dd.MM.yyyy HH:mm")
          : "-",
    },
    {
      field: "abbreviation",
      headerName: "Short Name",
      width: 300,
      sortable: false,
      renderCell: (row) => (
        <TextField
          sx={{ width: "100%" }}
          name="abbreviation"
          variant="outlined"
          size="small"
          label="Short Name"
          value={row.row.abbreviation || ""}
          disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_COMPETITORS)}
          onChange={({ target: { name, value } }) =>
            handleChange(row.id, name, value)
          }
        />
      ),
    },
    {
      field: "country",
      headerName: "Country",
      width: 300,
    },
    {
      field: "isDeactivated",
      headerName: "Deactivated",
      width: 100,
      renderCell: ({ row }) => (
        <Switch
          name="isDeactivated"
          onChange={({ target: { name, checked } }) =>
            handleChange(row.id, name, checked)
          }
          disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_COMPETITORS)}
          value={row.isDeactivated}
          checked={row.isDeactivated}
        />
      ),
    },
    {
      field: "visible",
      headerName: "Visible",
      sortable: false,
      width: 110,
      renderCell: (row) => row.row.visible
        ? <Chip color="success" variant="filled" size="medium" label={'Visible'} />
        : <Chip color="error" variant="filled" size="medium" label={'Not visible'} />
    },
    {
      field: "approved",
      headerName: "Approved",
      width: 120,
      sortable: false,
      renderCell: (row) =>
        <Button
          variant="contained"
          color={row.row.approved ? 'error' : 'success'}
          disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_COMPETITORS)}
          onClick={() => toggleApprove(row.row)}
        >
          {row.row.approved ? 'Unapprove' : 'Approve'}
        </Button>
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      renderCell: (row) => (
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variant="outlined"
            disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_COMPETITORS)}
            onClick={() => handleClick(row)}>
            Save
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_COMPETITORS)}
            onClick={() => handleOpenDialog(row.row)}
          >
            Edit
          </Button>
        </Box>
      ),
    },
  ];
};

export function TeamsBlock() {
  const projectId = useSelector((state) => state.project.currentProjectId);
  const currentSource = useSelector((state) => state.source.currentSource);
  const [competitors, setCompetitors] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [catalogDialog, setCatalogDialog] = useState(false);
  const [sortOptions, setSortOptions] = useState([
    { field: "name", sort: "asc" },
  ]);

  const [filters, setFilters] = useState({
    id: "",
    sport: "",
    name: "",
    country: "",
    urlSegment: "",
  });
  const [tempFilters, setTempFilters] = useState({
    id: "",
    sport: "",
    name: "",
    country: "",
    urlSegment: "",
  });

  const handleOpenDialog = useCallback((item) => {
    setCurrentTeam(item);
    setDialog(true);
  }, []);

  const closeDialog = () => {
    setDialog(false);
  };

  const handleOpenCatalogDialog = useCallback(() => {
    setCatalogDialog(true);
  }, []);

  const closeCatalogDialog = () => {
    setCatalogDialog(false);
  };

  const createContentKey = useCallback(() => {
    const contentKey = `Competitor-${currentTeam.id}`;
    return contentKey;
  }, [currentTeam]);

  const createParams = useCallback(() => {
    const filterParams = [];
    Object.keys(filters).forEach((key) => {
      if (!filters[key]) return;
      filterParams.push({ key, value: filters[key] });
    });
    const pageParams = [
      { key: "take", value: pageSize },
      { key: "skip", value: page * pageSize },
    ];
    const sortParams = [
      { key: "sortBy", value: sortOptions?.[0]?.field || "name" },
      { key: "sortDirection", value: sortOptions?.[0]?.sort || "asc" },
    ];
    const sourceParams = [{ key: "source", value: currentSource }];
    return [...filterParams, ...pageParams, ...sortParams, ...sourceParams];
  }, [filters, page, pageSize, sortOptions, currentSource]);

  const fetchCompetitorsList = useCallback(async () => {
    if (!currentSource) {
      return;
    }
    const queryParams = createParams();
    const competitors = await fetchAllCompetitors(queryParams);
    setCompetitors(competitors.data);
    setTotal(competitors.total);
  }, [createParams, currentSource]);

  const handleChange = useCallback(
    (id, name, value) => {
      const newRowIndex = [...competitors].findIndex((row) => row.id === id);
      const newCompetitors = [...competitors];
      newCompetitors[newRowIndex][name] = value;
      setCompetitors(newCompetitors);
    },
    [competitors]
  );

  const handleFiltersChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setTempFilters({ ...tempFilters, [name]: value });
    },
    [tempFilters]
  );

  const handleSearchClick = useCallback(() => {
    setPage(0);
    setFilters(tempFilters);
  }, [tempFilters]);

  const handleClick = useCallback(
    async ({ row }) => {
      const { id, abbreviation, isDeactivated } = row;
      const params = {
        abbreviation: abbreviation === "" ? null : abbreviation,
        isDeactivated,
      };
      await addCompetitorShortName(id, params);
      await fetchCompetitorsList();
    },
    [fetchCompetitorsList]
  );

  const handleSortModelChange = useCallback((sortModel) => {
    setPage(0);
    setSortOptions([...sortModel]);
  }, []);

  const handleResetFilters = useCallback(async () => {
    setFilters({
      sport: "",
      name: "",
      country: "",
      id: "",
      urlSegment: "",
    });
    setTempFilters({
      sport: "",
      name: "",
      country: "",
      id: "",
      urlSegment: "",
    });
    setPage(0);
    await fetchCompetitorsList();
  }, [fetchCompetitorsList]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPage(0);
    setPageSize(newPageSize);
  }, []);

  const handleGetCurrentTeamContent = useCallback(
    async (contentKey) => {
      const data = await fetchMatchCenterContent(projectId, 'cricket', contentKey);
      return data;
    },
    [projectId]
  );

  const handleSetCurrentTeamContent = useCallback(
    async (contentKey, content) => {
      const data = await setMatchCenterContent(projectId, 'cricket', contentKey, content);
      return data;
    },
    [projectId]
  );

  const handleGetCatalogContent = useCallback(
    async (contentKey) => {
      const data = await getContent(projectId, 'Competitor', contentKey);
      return data;
    },
    [projectId]
  );

  const handleSetCatalogContent = useCallback(
    async (contentKey, content) => {
      const data = await setContent(projectId, 'Competitor', contentKey, content);
      return data;
    },
    [projectId]
  );

  const contentKeys = [
    { title: "Teams", contentPostfix: "All" },
    { title: "Global", contentPostfix: "Global" },
    { title: "National", contentPostfix: "Countries" },
    { title: "Tournaments", contentPostfix: "Competitions" },
    { title: "Women", contentPostfix: "Women" },
    { title: "Men", contentPostfix: "Men" },
  ];

  useEffect(() => {
    fetchCompetitorsList();
  }, [fetchCompetitorsList]);

  const toggleApprove = useCallback(async (item) => {
    if (!item.approved) {
      await approveCompetitor(item.id);
    } else {
      await unapproveCompetitor(item.id);
    }
    fetchCompetitorsList();
  }, [fetchCompetitorsList]);

  const columns = useMemo(() => {
    return createColumns(handleChange, handleClick, handleOpenDialog, toggleApprove);
  }, [handleChange, handleClick, handleOpenDialog, toggleApprove]);

  return (
    <Box>
      {competitors && (
        <Box>
          <Box sx={{ display: "flex", gap: "25px", padding: "25px 0" }}>
            <h1 style={{ margin: 0 }}>Teams Catalog</h1>
            <PrivateComponent permission={PERMISSIONS.ALLOW_WRITE_COMPETITORS}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleOpenCatalogDialog()}
              >
                Edit Catalog Content
              </Button>
            </PrivateComponent>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "12px",
              padding: "20px 0",
            }}
          >
            filter by
            <TextField
              name="id"
              variant="outlined"
              size="small"
              label="ID"
              value={tempFilters.id}
              onChange={(event) => handleFiltersChange(event)}
            />
            <TextField
              name="sport"
              variant="outlined"
              size="small"
              label="Sport"
              value={tempFilters.sport}
              onChange={(event) => handleFiltersChange(event)}
            />
            <TextField
              name="name"
              variant="outlined"
              size="small"
              label="Name"
              value={tempFilters.name}
              onChange={(event) => handleFiltersChange(event)}
            />
            <TextField
              name="country"
              variant="outlined"
              size="small"
              label="Country"
              value={tempFilters.country}
              onChange={(event) => handleFiltersChange(event)}
            />
            <TextField
              name="urlSegment"
              variant="outlined"
              size="small"
              label="Url"
              value={tempFilters.urlSegment}
              onChange={(event) => handleFiltersChange(event)}
            />
            <Button variant="contained" onClick={() => handleSearchClick()}>
              Search
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleResetFilters()}
            >
              Reset
            </Button>
          </Box>
          <Box sx={{ height: "700px", width: "100%" }}>
            <DataGrid
              rows={competitors}
              columns={columns}
              rowCount={total}
              pageSize={pageSize}
              page={page}
              paginationMode="server"
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={[25, 50, 100]}
              sortingMode="server"
              onSortModelChange={handleSortModelChange}
              disableSelectionOnClick
              disableColumnFilter
              experimentalFeatures={{ newEditingApi: true }}
            />
          </Box>
        </Box>
      )}
      {currentTeam && (
        <Dialog fullScreen open={dialog} onClose={closeDialog}>
          <Box sx={{ padding: "25px" }}>
            <EditContent
              onSave={closeDialog}
              getContent={handleGetCurrentTeamContent}
              setContent={handleSetCurrentTeamContent}
              showFields={true}
              contentKey={createContentKey()}
            />
          </Box>
        </Dialog>
      )}
      <Dialog fullScreen open={catalogDialog} onClose={closeCatalogDialog}>
        <EditContentTabs
          item={{ contentKey: '' }}
          keys={contentKeys}
          closeDialog={closeCatalogDialog}
          getContent={handleGetCatalogContent}
          setContent={handleSetCatalogContent}
        />
      </Dialog>
    </Box>
  );
}
